import {
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getAllBranch, getAllRoutesByCompany } from "../../../services";
import { Branch } from "../../../types/user.type";
import { SaleRoute } from "../../../types/sale-route.type";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import ClearIcon from "@mui/icons-material/Clear";
import dayjs from "dayjs";
import "dayjs/locale/th";

dayjs.locale("th");

interface ReportFilterProps {
  selectedDate: Dayjs | null;
  setSelectedDate: Dispatch<SetStateAction<Dayjs | null>>;
  selectedBranch: string;
  setSelectedBranch: Dispatch<SetStateAction<string>>;
  selectedRoute: string;
  setSelectedRoute: Dispatch<SetStateAction<string>>;
  selectedBillId: string;
  setSelectedBillId: Dispatch<SetStateAction<string>>;
  taxId: string;
  setTaxId: Dispatch<SetStateAction<string>>;
  customerName: string;
  setCustomerName: Dispatch<SetStateAction<string>>;
  customerCode: string;
  setCustomerCode: Dispatch<SetStateAction<string>>;
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <Grid size={{ xs: 12, sm: 4 }}>
    <Grid container alignItems="center" spacing={1}>
      <Grid size={2}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Grid>
      <Grid size={10}>{children}</Grid>
    </Grid>
  </Grid>
);

export default function ReportFilter({
  selectedDate,
  setSelectedDate,
  selectedBranch,
  setSelectedBranch,
  selectedRoute,
  setSelectedRoute,
  selectedBillId,
  setSelectedBillId,
  taxId,
  setTaxId,
  customerName,
  setCustomerName,
  customerCode,
  setCustomerCode,
}: ReportFilterProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<SaleRoute[]>([]);

  const onGetAllBranch = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, []);

  const onGetAllRoutes = useCallback(async () => {
    const {
      data: { data },
    } = await getAllRoutesByCompany({ includeSpecial: true });
    setRoutes(data);
  }, []);

  useEffect(() => {
    if (isHeadOfficeAccounting) {
      onGetAllBranch();
    }
    onGetAllRoutes();
  }, [isHeadOfficeAccounting, onGetAllBranch, onGetAllRoutes]);

  // HO selects a branch first, then sees only that branch's routes.
  // Branch users are already scoped to their own branch by the API.
  const routeOptions = useMemo(() => {
    if (!isHeadOfficeAccounting) return routes;
    if (selectedBranch === "all") return [];
    const branchUuid = branches.find(
      (branch) => branch.branch_code === selectedBranch,
    )?.uuid;
    return routes.filter((route) => route.branch_uuid === branchUuid);
  }, [isHeadOfficeAccounting, routes, branches, selectedBranch]);

  const isRouteDisabled = isHeadOfficeAccounting && selectedBranch === "all";

  const handleChangeBranch = (branchCode: string) => {
    setSelectedBranch(branchCode);
    // Reset route when branch changes since routes depend on the branch.
    setSelectedRoute("all");
  };

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField label="วันที่">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <DatePicker
              value={selectedDate}
              onChange={setSelectedDate}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { size: "small", fullWidth: true },
              }}
            />
            {selectedDate && (
              <IconButton
                size="small"
                sx={{ ml: 0.5 }}
                onClick={() => setSelectedDate(null)}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </LocalizationProvider>
      </FilterField>
      {isHeadOfficeAccounting && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={selectedBranch}
            onChange={(e) => handleChangeBranch(e.target.value)}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={selectedRoute}
          onChange={(e) => setSelectedRoute(e.target.value)}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {routeOptions.map((route) => (
            <MenuItem key={route.route_code} value={route.route_code}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="ใบแจ้งหนี้">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอกใบแจ้งหนี้"
          value={selectedBillId}
          onChange={(e) => setSelectedBillId(e.target.value)}
        />
      </FilterField>
      {isHeadOfficeAccounting && (
        <>
          <FilterField label="Tax ID">
            <TextField
              size="small"
              fullWidth
              placeholder="กรอก Tax ID"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
            />
          </FilterField>
          <FilterField label="ชื่อลูกค้า">
            <TextField
              size="small"
              fullWidth
              placeholder="กรอกชื่อลูกค้า"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </FilterField>
          <FilterField label="รหัสลูกค้า">
            <TextField
              size="small"
              fullWidth
              placeholder="กรอกรหัสลูกค้า"
              value={customerCode}
              onChange={(e) => setCustomerCode(e.target.value)}
            />
          </FilterField>
        </>
      )}
    </Grid>
  );
}
